import React from "react";
import PropTypes, { arrayOf, number, shape } from "prop-types";
import MapShape from "components/MapShape";
import mapF1StaticData from "config/f1Info";
import mapF2StaticData from "config/f2Info";
import mapF3StaticData from "config/f3Info";
import elevators from "config/elevators.json";
import slotsApiMapping from "config/slotsApiMapping.json";
import playClickSpecial from "utils/playClickSpecial";

import { ReactComponent as PinActive } from "assets/images/icon_pin_active.svg";
import { ReactComponent as PinInActive } from "assets/images/icon_pin_inactive.svg";

import styles from "./styles.module.css";
import { getCachedItem } from "utils/localStorage";
import {
  deviceFloorLevel,
  deviceSvgId,
  mapOrientation,
} from "config/constants";
import useGetFloorLvl from "hooks/useGetFloorLvl";
import MapTravelBar from "components/MapTravelBar/MapTravelBar";
import MapBanner from "components/MapBanner/MapBanner";
import { searchNearestCommutation } from "./localLogic";

MapRouter.propTypes = {
  locationsHighlighted: PropTypes.string,
  floorIndex: PropTypes.number,
  rotate: PropTypes.number,
  slotInfo: PropTypes.shape({
    floorLevel: number,
    svgId: number,
  }),
  pinsList: arrayOf(
    shape({
      x: number,
      y: number,
    })
  ),
};

function MapRouter(props) {
  const { slotInfo, rotate, bannerSrc, layout, destinationName } = props;
  const mapRotation =
    rotate || rotate === 0 ? rotate : getCachedItem(mapOrientation);
  const verticalMap = (mapRotation / 90) % 2 === 1;
  const deviceFloorLevelValue = useGetFloorLvl();
  const deviceLocation = getCachedItem(deviceSvgId);

  const [isTargetOnDifferntFloor, setIsTargetOnDifferntFloor] =
    React.useState(false);
  const [shouldShowMainFloor, setShouldShowMainFloor] = React.useState(true);

  /**
   *  state of  MAIN FLOOR (WHERE THE DEVICE LOCATED )
   */

  const deviceFloor = getCachedItem(deviceFloorLevel);

  const [mapInfoSetOfMainFloor, setMapInfoSetOfMainFloor] = React.useState({
    floorIndex: deviceFloor,
    vertices: [],
    connections: [],
  });
  const [startPointOfMainFloor, setStartPointOfMainFloor] =
    React.useState(null);
  const [endPointOfMainFloor, setEndPointOfMainFloor] = React.useState(null);
  const [commuteByEscalator, setCommuteByEscalator] = React.useState(false);
  // Cost will be calculated into meter
  const [costToDestinationOfMainFloor, setCostToDestinationOfMainFloor] =
    React.useState(0);
  /** \\  state of  MAIN FLOOR (WHERE THE DEVICE LOCATED )  */

  /**
   * state of the DESTINATION FLOOR
   */

  const [mapInfoSetOfDestinationFloor, setMapInfoSetOfDestinationFloor] =
    React.useState({
      floorIndex: 1,
      vertices: [],
      connections: [],
    });
  const [startPointOfDestinationFloor, setStartPointOfDestinationFloor] =
    React.useState(null);
  const [endPointOfDestinationFloor, setEndPointOfDestinationFloor] =
    React.useState(null);
  // Cost will be calculated into meter
  const [
    costToDestinationOfDestinationFloor,
    setCostToDestinationOfDestinationFloor,
  ] = React.useState(0);
  /** \\ state of the DESTINATION FLOOR  */

  const getMapInfoSet = (floorNumber) => {
    // setState to be the static DB relevant floor ( including coordinates and connections )
    switch (floorNumber) {
      case 1:
        return mapF1StaticData;
      case 2:
        return mapF2StaticData;
      case 3:
        return mapF3StaticData;
    }
  };

  const pathToElevator = () => {
    if (isTargetOnDifferntFloor) {
      playClickSpecial();
      setShouldShowMainFloor(true);
    }
  };

  const pathToDestination = () => {
    if (isTargetOnDifferntFloor) {
      playClickSpecial();
      setShouldShowMainFloor(false);
    }
  };

  const handleTitleClick = (titleData) => {
    const { id, floorLevel } = titleData;
    // Find the corresponding svgId from slotsApiMapping
    const slotMapping = slotsApiMapping.find(slot => slot.id === id && slot.floorLevel === floorLevel);
    if (slotMapping) {
      playClickSpecial();
      // For same floor navigation, set the end point directly
      if (floorLevel === deviceFloor) {
        setEndPointOfMainFloor(slotMapping.svgId);
      } else {
        // For different floor, this would require more complex logic
        // For now, we'll focus on same-floor navigation
        console.log('Cross-floor navigation from title click not yet implemented');
      }
    }
  };

  React.useEffect(() => {
    setMapInfoSetOfMainFloor(getMapInfoSet(deviceFloor));

    // slotInfo === destination slot information including floorLevel and svgId
    if (slotInfo && deviceFloor !== slotInfo.floorLevel) {
      // If the floor where set device is different from the destination floor
      setIsTargetOnDifferntFloor(true);
      const dataOfDestinationFloor = getMapInfoSet(slotInfo.floorLevel);
      setMapInfoSetOfDestinationFloor(dataOfDestinationFloor);
    } else {
      setIsTargetOnDifferntFloor(false);
    }
  }, ["didMount", slotInfo]);

  React.useEffect(() => {
    setStartPointOfMainFloor(deviceLocation);
    if (!deviceFloor || !deviceLocation) {
      return;
    }
    if (isTargetOnDifferntFloor) {
      // Show path guide to the elevator on the DESTINATION FLOOR
      const targetElevatorId = searchNearestCommutation(
        deviceLocation,
        deviceFloor,
        slotInfo?.floorLevel
      )?.id;
      const targetElevatorItem = elevators.find(
        ({ id }) => id === targetElevatorId
      );
      // [Get-out point] Still the same elevator/escalator but on destination floor
      setStartPointOfDestinationFloor(
        targetElevatorItem.svgIdOnFloor[slotInfo?.floorLevel]
      );
      // [Enter point] Targeted elevator/escalator on the device's floor
      setEndPointOfMainFloor(targetElevatorItem.svgIdOnFloor[deviceFloor]);
      // Change icon to elevator if the transportation is not escalator
      setCommuteByEscalator(targetElevatorItem.isEscalator);
      setEndPointOfDestinationFloor(slotInfo?.svgId);
    } else if (slotInfo && slotInfo.floorLevel === deviceFloor) {
      setEndPointOfMainFloor(slotInfo?.svgId);
    }
  }, [isTargetOnDifferntFloor, slotInfo]);

  if (!mapInfoSetOfMainFloor || !mapInfoSetOfMainFloor.vertices) {
    return null;
  }

  return (
    <div
      className={`${styles.map_layout} ${
        verticalMap ? styles.vertical_layout : ""
      } ${layout ? styles[layout] : ""}`}
    >
      <div className={styles.map_wrapper}>
        {!isTargetOnDifferntFloor || shouldShowMainFloor ? (
          <MapShape
            {...props}
            rotate={mapRotation}
            // className={
            //   !shouldShowMainFloor ?
            //   styles.local_display_none
            //   : styles.local_display_visible
            // }
            // If there are two floors to traverse then this "end" is not a destination but an elevator/escalator
            start={startPointOfMainFloor}
            end={endPointOfMainFloor}
            vertices={mapInfoSetOfMainFloor?.vertices}
            floorIndex={mapInfoSetOfMainFloor?.floorIndex}
            getTravelCost={setCostToDestinationOfMainFloor}
            connections={mapInfoSetOfMainFloor?.connections}
            key={mapInfoSetOfMainFloor?.floorIndex + "_floor"}
            notDestinationButElevator={isTargetOnDifferntFloor}
            onTitleClick={handleTitleClick}
          />
        ) : !shouldShowMainFloor ? (
          <MapShape
            {...props}
            rotate={mapRotation}
            start={startPointOfDestinationFloor}
            end={endPointOfDestinationFloor}
            vertices={mapInfoSetOfDestinationFloor?.vertices}
            floorIndex={mapInfoSetOfDestinationFloor?.floorIndex}
            getTravelCost={setCostToDestinationOfDestinationFloor}
            connections={mapInfoSetOfDestinationFloor?.connections}
            key={mapInfoSetOfDestinationFloor?.floorIndex + "_floor"}
            onTitleClick={handleTitleClick}
          />
        ) : (
          <></>
        )}
        {/* <div
          className={
            !shouldShowMainFloor ?
            styles.local_display_none
            : styles.local_display_visible
          }
        >
          <MapShape
            {...props}
            rotate={mapRotation}
            className={
              !shouldShowMainFloor ?
              styles.local_display_none
              : styles.local_display_visible
            }
            // If there are two floors to traverse then this "end" is not a destination but an elevator/escalator
            start={startPointOfMainFloor}
            end={endPointOfMainFloor}
            vertices={mapInfoSetOfMainFloor?.vertices}
            floorIndex={mapInfoSetOfMainFloor?.floorIndex}
            getTravelCost={setCostToDestinationOfMainFloor}
            connections={mapInfoSetOfMainFloor?.connections}
            key={mapInfoSetOfMainFloor?.floorIndex + "_floor"}
            notDestinationButElevator={isTargetOnDifferntFloor}
          />
        </div>
        <div
          className={
            !isTargetOnDifferntFloor || shouldShowMainFloor
            ? styles.local_display_none
            : styles.local_display_visible
          }
        >
          <MapShape
            {...props}
            rotate={mapRotation}
            start={startPointOfDestinationFloor}
            end={endPointOfDestinationFloor}
            vertices={mapInfoSetOfDestinationFloor?.vertices}
            floorIndex={mapInfoSetOfDestinationFloor?.floorIndex}
            getTravelCost={setCostToDestinationOfDestinationFloor}
            connections={mapInfoSetOfDestinationFloor?.connections}
            key={mapInfoSetOfDestinationFloor?.floorIndex + "_floor"}
          />
        </div> */}
      </div>

      {!props.hideTravelBar && (
        <>
          <div className={styles.map_sidebar}>
            <div className={styles.map_sidebar_left}>
              <MapTravelBar
                isOnDestination={!shouldShowMainFloor}
                vertical={verticalMap}
                layout={layout}
                commuteByEscalator={commuteByEscalator}
                pathToElevator={pathToElevator}
                pathToDestination={pathToDestination}
                destinationName={destinationName}
                isTargetOnDifferntFloor={isTargetOnDifferntFloor}
                currentFloorLevel={deviceFloorLevelValue}
                destinationFloorLevel={slotInfo?.floorLevel}
                costToDestinationOfMainFloor={costToDestinationOfMainFloor}
                costToDestinationOfDestinationFloor={
                  costToDestinationOfDestinationFloor
                }
              />
            </div>
            <div className={styles.map_sidebar_right}>
              {verticalMap && layout === "restaurant" ? null : (
                <MapBanner
                  bannerSrc={bannerSrc}
                  width={verticalMap || layout ? "100%" : "80%"}
                />
              )}
            </div>
          </div>
          {verticalMap && layout === "restaurant" && (
            <div className={styles.banner_wrapper}>
              <MapBanner bannerSrc={bannerSrc} width={"100%"} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

MapRouter.defaultProps = {
  floorIndex: getCachedItem(deviceFloorLevel),
};

export default React.memo(MapRouter);
